import { NgClass, NgStyle } from '@angular/common';
import { Component, DestroyRef, ElementRef, forwardRef, inject, Input, Provider, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

const INLINE_EDITOR_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InlineEditorComponent),
  multi: true,
};

@Component({
  selector: 'app-inline-editor',
  imports: [NgClass, NgStyle, FormsModule, MatDialogTitle, MatIconModule, ReactiveFormsModule, MatTooltipModule],
  templateUrl: './inline-editor.component.html',
  styleUrls: ['./inline-editor.component.scss'],
  providers: [INLINE_EDITOR_VALUE_ACCESSOR],
})
export class InlineEditorComponent {
  @Input() editIconTooltip: string | undefined;
  @Input() viewHeader: string | undefined;
  @Input() editHeader: string | undefined;
  @Input() headerIcon: string | undefined;
  @Input() headerIconTooltip: string | undefined;
  @Input() allowEdit = false;
  @Input() showEditIcon = false;
  @Input() placeholder: string | undefined;
  @Input() viewOpacity: number | undefined;
  @Input() set errorMessage(errorMessage: string | undefined) {
    this.error = errorMessage;
    if (this.error) {
      this.onEdit();
    }
  }

  formControl = new FormControl();
  value: any;
  valueBeforeEdit: any;
  editing = false;
  disabled = false;
  error: string | undefined;

  @ViewChild('editInput') editInput: ElementRef | undefined;

  private destroyRef = inject(DestroyRef);

  onEdit(): void {
    this.valueBeforeEdit = this.formControl.value;
    this.setEditing(true);
    setTimeout(() => {
      this.editInput?.nativeElement.focus();
    });
  }

  onCancelEdit(event: KeyboardEvent): void {
    event.stopPropagation();
    this.formControl.setValue(this.valueBeforeEdit, { emitEvent: false });
    this.value = this.valueBeforeEdit;
    this.onChange(this.valueBeforeEdit);
    this.setEditing(false);
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
    this.formControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;

    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.value = value;
        this.onChange(value);
      });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  setEditing(editing: boolean): void {
    if (!editing && this.error) {
      this.onEdit();
      return;
    }
    this.editing = editing;
  }
}